import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { startLoading, stopLoading } from "./commonSlice";
import { fromWithCompany, supabase } from "../config/supabaseClient";
import axios from "axios";
import moment from "moment";
import { statuses, trainingFormStatus, DEFAULT_FORM_SUBMIT_COMMENT } from "../constant/constants";
import {
  formType,
  getDepartmentId,
  getLocalData,
  sendEmailNotification,
} from "../constant/utils/commonUtils";
import { currentDate } from "../constant/utils/datetimeUtils";
import { fetchOrganogram, fetchOrganogramByDepartmentId } from "./organogramSlice";
import { roleType } from "../constant/constants";
import { isCompanyPrivileged } from "../utils/roleUtils";
import { toast } from "react-toastify";

async function resolveFormManagerRouting(
  dispatch,
  { companyId, departmentId, submitterRole, submitterEmpId },
) {
  const normalizedDeptId = departmentId ? String(departmentId).trim() : "";
  if (!normalizedDeptId || normalizedDeptId.toLowerCase() === "null") {
    return { deptId: null, dept_head: null, assigneeRole: "" };
  }

  const organogramData = await dispatch(fetchOrganogram(companyId));
  const nodes = organogramData.payload || [];
  if (!nodes.length) {
    return { deptId: null, dept_head: null, assigneeRole: "" };
  }

  const findDept = nodes.find(
    (item) =>
      String(item.departmentId ?? "").trim() === normalizedDeptId,
  );
  if (!findDept) {
    return { deptId: null, dept_head: null, assigneeRole: "" };
  }

  let deptId = null;
  let dept_head = null;
  if (
    findDept.approvalRequired &&
    findDept.departmentId &&
    findDept.departmentHeadId &&
    (submitterRole === roleType.MANAGER
      ? submitterEmpId !== findDept.departmentHeadId
      : true)
  ) {
    deptId = findDept.departmentId;
    dept_head = findDept.departmentHeadId;
  } else {
    const dept = getDepartmentId(findDept.parentDepartmentId, nodes);
    deptId = dept?.deptId ?? null;
    dept_head = dept?.deptHeadId ?? null;
  }

  let assigneeRole = "";
  if (dept_head) {
    const { data: managerUser } = await fromWithCompany("company_user")
      .eq("emp_id", dept_head)
      .eq("status", statuses.ACTIVE)
      .maybeSingle();
    assigneeRole = managerUser?.role || "";
  }

  return {
    deptId: deptId ? String(deptId) : null,
    dept_head,
    assigneeRole,
  };
}

export const addTrainingForm = createAsyncThunk(
  "trainingForm/addTrainingForm",
  async (
    { trainingForm, required_skills, saveType, filled_by_sdf_on_behalf },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(startLoading());
      const { data: form, error: formError } = await supabase
        .from("training_survey_form")
        .insert(trainingForm)
        .select("*,company_user(first_name,last_name,department_id)")
        .single();

      if (formError) throw formError;

      // Process skills sequentially to avoid callback issues
      for (const skill of required_skills) {
        // Insert skill requirement
        const skill_data = {
          skills_required: skill.skillsRequired?.trim(),
          training_type: skill.trainingType,
          priority_level: skill.priorityLevel,
          skills_requirement_motivation:
            skill.skillsRequirementMotivation?.trim(),
          completion_date: skill.completionDate ? moment(skill.completionDate).format('YYYY-MM-DD HH:mm:ss') : null,
          program_duration: skill.programmeDuration || skill.programDuration || null,
          form_id: form.id,
        };

        const { data: skillData, error: skillError } = await supabase
          .from("skill_requirements")
          .insert(skill_data)
          .select()
          .single();

        if (skillError) throw skillError;

        // Insert trainings sequentially
        for (const trainingId of skill.selectedTrainings) {
          const training_data = {
            training_id: trainingId,
            skill_requirements_id: skillData.id,
            form_id: form.id,
            company_id: form.company_id,
            year_id: form.year_id,
          };

          const { data: trainingData, error: trainingError } = await supabase
            .from("selected_trainings")
            .insert(training_data)
            .select()
            .single();

          if (trainingError) throw trainingError;

          // details.selectedTrainings.push({
          //   id: trainingData.id,
          //   trainingId: trainingData.training_id,
          // });

          // formattedData.skillsDetail.push(details);
        }
      }
      const userData = getLocalData();
      let deptId = null;
      let dept_head = null;
      let latestFormStatus = { status: "", to_id: "", role: "" };
      let initialFormStatusId = null;
      let historyAction = saveType;

      const isPrivilegedOnBehalf =
        filled_by_sdf_on_behalf &&
        form.company_user?.emp_id &&
        userData.emp_id &&
        form.company_user.emp_id !== userData.emp_id;

      // SDF on-behalf only: skip manager. Own form routes to dept head.
      const bypassApproval =
        isPrivilegedOnBehalf &&
        userData.role === roleType.SDF &&
        saveType === trainingFormStatus.REQUESTED;

      if (bypassApproval) {
        const { data: formStatusData, error: formStatusError } = await supabase
          .from("form_status")
          .insert({
            form_id: form.id,
            status: trainingFormStatus.RECOMMENDED,
            created_by: userData.id,
            from_id: userData.id,
            to_id: null,
            updated_by: userData.id,
            updated_at: currentDate(),
            role: isCompanyPrivileged(userData.role)
              ? userData.role
              : roleType.SDF,
          })
          .select()
          .single();
        if (formStatusError) throw formStatusError;
        initialFormStatusId = formStatusData.id;
        historyAction = formStatusData.status;
        latestFormStatus = { status: formStatusData.status, to_id: formStatusData.to_id, role: formStatusData.role };
      } else if (saveType !== trainingFormStatus.DRAFT) {
        const routing = await resolveFormManagerRouting(dispatch, {
          companyId: userData.company_id,
          departmentId:
            form.company_user?.department_id || userData.department_id,
          submitterRole: userData.role,
          submitterEmpId: userData.emp_id,
        });
        deptId = routing.deptId;
        dept_head = routing.dept_head;
      }

      let formAssignee = {};
      if (!bypassApproval) {
        if (deptId && dept_head) {
          const { data: user, error: userError } = await fromWithCompany(
            "company_user"
          )
            .eq("emp_id", dept_head)
            .eq("status", statuses.ACTIVE)
            .single();

          if (userError && userError?.code !== "PGRST116") throw userError;
          formAssignee = {
            email: user?.email,
            first_name: user?.first_name,
            last_name: user?.last_name,
            data: user?.data,
            role: user?.role,
          };
        }

        const { data: formStatusData, error: formStatusError } = await supabase
          .from("form_status")
          .insert({
            form_id: form.id,
            status: saveType,
            created_by: userData.id,
            from_id: userData.id,
            to_id: deptId || null,
            updated_by: userData.id,
            updated_at: currentDate(),
            role: deptId
              ? formAssignee.role
              : saveType === trainingFormStatus.REQUEST_EDIT
                ? ""
                : userData.role === roleType.SDF
                  ? roleType.SDF
                  : "",
          })
          .select()
          .single();

        if (formStatusError) throw formStatusError;
        initialFormStatusId = formStatusData.id;
        latestFormStatus = {
          status: formStatusData.status,
          to_id: formStatusData.to_id || null,
          role: formStatusData.role,
        };

        if (
          !deptId &&
          saveType !== trainingFormStatus.DRAFT &&
          userData.role !== roleType.COMPANY_ADMIN &&
          userData.role !== roleType.SDF
        ) {
        const { data: recommendedStatusData, error: recommendedStatusError } = await supabase
          .from("form_status")
          .insert({
            form_id: form.id,
            status: trainingFormStatus.RECOMMENDED,
            created_by: userData.id,
            from_id: userData.id,
            to_id: deptId || null,
            updated_by: userData.id,
            updated_at: currentDate(),
            role: deptId ? formAssignee?.role : "",
          })
          .select()
          .single();

        if (recommendedStatusError) throw recommendedStatusError;
        latestFormStatus = {
          status: recommendedStatusData.status,
          to_id: recommendedStatusData.to_id || null,
          role: recommendedStatusData.role,
        };
        }

        if (formAssignee?.email) {
        // sendEmailNotification(
        //   formAssignee.email,
        //   "For the Approval of Training Form",
        //   `Training Form Submitted by ${
        //     form.company_user.first_name + " " + form.company_user.last_name ||
        //     "Unknown"
        //   }`
        // );
          sendEmailNotification(formAssignee.email, formType.REQUESTED, {
            empName:
              form.company_user.first_name + " " + form.company_user.last_name ||
              "Unknown",
            dept_id: form.company_user.department_id,
            formId: form.id,
            company_id: form.company_id,
          });
        }
      }
      if (saveType !== trainingFormStatus.DRAFT && initialFormStatusId) {
        const { error: historyError } = await supabase.rpc(
          "record_form_submit_history",
          {
            p_form_id: form.id,
            p_form_status_id: initialFormStatusId,
            p_action: historyAction,
            p_created_by: userData.id,
            p_comment: DEFAULT_FORM_SUBMIT_COMMENT,
          },
        );
        if (historyError) throw historyError;
      }
      const deptLookupId =
        latestFormStatus?.to_id || form.company_user?.department_id;
      const department = deptLookupId
        ? await dispatch(fetchOrganogramByDepartmentId(deptLookupId))
        : null;
      return {
        form,
        assignee_role: latestFormStatus.role,
        form_status: latestFormStatus.status,
        to_id: latestFormStatus.to_id,
        dept_head_id:
          latestFormStatus.status === trainingFormStatus.DRAFT
            ? getLocalData().emp_id
            : department?.payload?.dept_head_id,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateTrainingForm = createAsyncThunk(
  "trainingForm/updateTrainingForm",
  async (
    { trainingForm, required_skills, form_id, to_id = "", saveType, filled_by_sdf_on_behalf },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(startLoading());

      const { data: form, error: formError } = await supabase
        .from("training_survey_form")
        .update(trainingForm)
        .eq("id", form_id)
        .select(
          "*,company_user(first_name,last_name,emp_id,email,department_id,employment_type)"
        )
        .single();

      if (formError) throw formError;

      const { data: trainings, error: trainingsError } = await supabase
        .from("selected_trainings")
        .delete()
        .eq("form_id", form_id);

      if (trainingsError && trainingsError.code != "PGRST116")
        throw trainingsError;

      const { data: skills, error: skillsError } = await supabase
        .from("skill_requirements")
        .delete()
        .eq("form_id", form_id);

      if (skillsError && skillsError.code != "PGRST116") throw skillsError;

      for (const skill of required_skills) {
        // Insert skill requirement
        const skill_data = {
          skills_required: skill.skillsRequired?.trim(),
          training_type: skill.trainingType,
          priority_level: skill.priorityLevel,
          skills_requirement_motivation:
            skill.skillsRequirementMotivation?.trim(),
          completion_date:
            skill.completionDate ?
            moment(skill.completionDate).format("YYYY-MM-DD HH:mm:ss") : null,
          program_duration: skill.programmeDuration || skill.programDuration || null,
          form_id: form_id,
        };

        const { data: skillData, error: skillError } = await supabase
          .from("skill_requirements")
          .insert(skill_data)
          .select()
          .single();

        if (skillError) throw skillError;

        if (!skill.selectedTrainings) continue;
        // Insert trainings sequentially
        for (const trainingId of skill?.selectedTrainings) {
          const training_data = {
            training_id: trainingId,
            skill_requirements_id: skillData.id,
            form_id: form.id,
            company_id: form.company_id,
            year_id: form.year_id,
          };

          const { data: trainingData, error: trainingError } = await supabase
            .from("selected_trainings")
            .insert(training_data)
            .select()
            .single();

          if (trainingError) throw trainingError;
        }
      }
      const userData = getLocalData();
      let deptId = null;
      let dept_head = null;
      let saveAndApprove;
      const isOnBehalfOfEmployee =
        form.company_user?.emp_id &&
        userData.emp_id &&
        form.company_user.emp_id !== userData.emp_id;

      const needsDeptRouting =
        saveType !== trainingFormStatus.DRAFT &&
        saveType !== trainingFormStatus.REQUEST_EDIT &&
        (!isCompanyPrivileged(userData.role) ||
          isOnBehalfOfEmployee ||
          userData.role === roleType.COMPANY_ADMIN ||
          userData.role === roleType.SDF);

      if (needsDeptRouting) {
        saveAndApprove = userData.emp_id != form.company_user.emp_id;

        if (
          isOnBehalfOfEmployee ||
          userData.role === roleType.COMPANY_ADMIN ||
          userData.role === roleType.SDF ||
          !saveAndApprove
        ) {
          const routing = await resolveFormManagerRouting(dispatch, {
            companyId: userData.company_id,
            departmentId:
              form.company_user?.department_id || userData.department_id,
            submitterRole: userData.role,
            submitterEmpId: userData.emp_id,
          });
          deptId = routing.deptId;
          dept_head = routing.dept_head;
        } else {
          const organogramData = await dispatch(
            fetchOrganogram(userData.company_id),
          );
          const routingDeptId = saveAndApprove ? to_id : userData.department_id;
          const findDept = routingDeptId
            ? organogramData.payload?.find(
                (item) =>
                  String(item.departmentId ?? "").trim() ===
                  String(routingDeptId).trim(),
              )
            : null;

          if (findDept) {
            if (
              saveAndApprove &&
              (isCompanyPrivileged(userData.role) ||
                (userData.role == roleType.MANAGER &&
                  userData.emp_id == findDept.departmentHeadId))
            ) {
              deptId = findDept.parentDepartmentId;
              dept_head = findDept.departmentHeadId;
            } else {
              deptId = findDept?.departmentId;
              dept_head = findDept?.departmentHeadId;
            }
            const dept = getDepartmentId(deptId, organogramData.payload);
            deptId = dept?.deptId;
            dept_head = dept?.deptHeadId;
          }
        }
      }
      // Send Email Notification
      let formAssignee = {};
      if (deptId && dept_head) {
        const { data: user, error: userError } = await fromWithCompany(
          "company_user"
        )
          .eq("emp_id", dept_head)
          .eq("status", statuses.ACTIVE)
          .single();

        if (userError) throw userError;
        formAssignee = {
          email: user?.email,
          first_name: user?.first_name,
          last_name: user?.last_name,
          data: user?.data,
          role: user?.role,
        };
      }

      const sdfOnBehalfApproved =
        isOnBehalfOfEmployee &&
        userData.role === roleType.SDF &&
        saveType === trainingFormStatus.REQUESTED;
      const statusToInsert = sdfOnBehalfApproved
        ? trainingFormStatus.RECOMMENDED
        : (saveType ? saveType : saveAndApprove ? trainingFormStatus.RECOMMENDED : trainingFormStatus.REQUESTED);

      const { data: formStatusData, error: formStatusError } = await supabase
        .from("form_status")
        .insert({
          form_id: form.id,
          status: statusToInsert,
          created_by: userData.id,
          from_id: userData.id,
          to_id: deptId || null,
          updated_by: userData.id,
          updated_at: currentDate(),
          role: deptId ? formAssignee.role : "",
        })
        .select()
        .single();

      if (formStatusError) throw formStatusError;
      const data = {
        empName:
          form.company_user.first_name + " " + form.company_user.last_name ||
          "Unknown",
        dept_id: form.company_user.department_id,
        managerName:
          getLocalData()?.first_name + " " + getLocalData()?.last_name ||
          "Unknown",
        formId: form.id,
        company_id: form.company_id,
      };
      if (formStatusData.status == trainingFormStatus.REQUEST_EDIT) {
        sendEmailNotification(
          form.company_user.email,
          saveType,
          data
        );
      } else if (
        !sdfOnBehalfApproved &&
        formStatusData.status !== trainingFormStatus.APPROVED
      ) {
        formAssignee?.email &&
          // sendEmailNotification(
          //   formAssignee.email,
          //   "For the Approval of Training Form",
          //   `Training Form ${
          //     formStatusData.status == trainingFormStatus.REQUESTED ? "Submitted by " + form.company_user.first_name + " " + form.company_user.last_name : "Approved/Updated by " + formAssignee.first_name + " " + formAssignee.last_name || formAssignee.data?.Name || "Unknown"
          //     }`
          //   );
          sendEmailNotification(formAssignee.email, saveType, data);
      }

      // if (saveAndApprove) {
      //   const { data, error } = await supabase
      //     .from("comments")
      //     .insert({
      //       form_id: form_id,
      //       comment: "Approved",
      //       type: "comment",
      //       created_by: userData.id,
      //     })
      //     .select()
      //     .single();

      //   if (error) throw error;
      // }
      
      const deptLookupId =
        formStatusData?.to_id || form.company_user?.department_id;
      const department = deptLookupId
        ? await dispatch(fetchOrganogramByDepartmentId(deptLookupId))
        : null;

      return {
        form,
        assignee_role: formStatusData.role,
        form_status: formStatusData.status,
        to_id: formStatusData.to_id,
        dept_head_id:
          formStatusData.status === trainingFormStatus.REQUEST_EDIT ||
          formStatusData.status === trainingFormStatus.DRAFT
            ? form.company_user.emp_id
            : department?.payload?.dept_head_id,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

const mapSkillsForHistoryRpc = (required_skills = []) =>
  required_skills.map((skill) => ({
    skillsRequired: skill.skillsRequired,
    trainingType: skill.trainingType,
    priorityLevel: skill.priorityLevel,
    skillsRequirementMotivation: skill.skillsRequirementMotivation,
    completionDate: skill.completionDate,
    programDuration: skill.programmeDuration || skill.programDuration,
    selectedTrainings: skill.selectedTrainings || [],
  }));

export const saveTrainingFormWithHistory = createAsyncThunk(
  "trainingForm/saveTrainingFormWithHistory",
  async (
    {
      form_id,
      target_status,
      comment,
      training_form,
      required_skills,
      to_id,
      role,
      write_history_on_data_only = false,
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(startLoading());
      const userData = getLocalData("sdfUser") || getLocalData();
      let resolvedToId =
        to_id != null && String(to_id).trim().toLowerCase() !== "null"
          ? String(to_id)
          : null;
      let resolvedRole = role || "";

      if (
        !resolvedToId &&
        target_status === trainingFormStatus.REQUESTED &&
        form_id
      ) {
        const { data: formRow } = await supabase
          .from("training_survey_form")
          .select("company_user(department_id)")
          .eq("id", form_id)
          .single();
        const routing = await resolveFormManagerRouting(dispatch, {
          companyId: userData.company_id,
          departmentId: formRow?.company_user?.department_id,
          submitterRole: userData.role,
          submitterEmpId: userData.emp_id,
        });
        resolvedToId = routing.deptId;
        resolvedRole = routing.assigneeRole || resolvedRole;
      }

      const resolvedComment =
        comment?.trim() ||
        (target_status === trainingFormStatus.REQUESTED
          ? DEFAULT_FORM_SUBMIT_COMMENT
          : null);
      const { data, error } = await supabase.rpc(
        "save_training_form_with_history",
        {
          p_form_id: form_id,
          p_target_status: target_status || null,
          p_comment: resolvedComment,
          p_action: target_status || null,
          p_created_by: userData.id,
          p_from_id: userData.id,
          p_to_id: resolvedToId,
          p_role: resolvedRole,
          p_training_form: training_form || null,
          p_skills: required_skills
            ? mapSkillsForHistoryRpc(required_skills)
            : null,
          p_write_history_on_data_only: write_history_on_data_only,
        }
      );
      if (error) throw error;
      return {
        form_status: data?.status || target_status,
        form_status_id: data?.form_status_id,
        form_history_id: data?.form_history_id,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const bulkUpdateFormStatus = createAsyncThunk(
  "trainingForm/bulkUpdateFormStatus",
  async ({ items, comment }, { rejectWithValue, dispatch }) => {
    try {
      if (!items?.length) {
        return rejectWithValue("No forms selected");
      }
      if (!comment?.trim()) {
        return rejectWithValue("Comment is required");
      }

      const allowedTargets = new Set([
        trainingFormStatus.APPROVED,
        trainingFormStatus.RECOMMENDED,
        trainingFormStatus.REQUESTED,
        trainingFormStatus.REQUEST_EDIT,
      ]);
      for (const item of items) {
        if (!item.formId || !allowedTargets.has(item.targetStatus)) {
          return rejectWithValue("Invalid form status transition");
        }
      }

      dispatch(startLoading());
      const userData = getLocalData("sdfUser") || getLocalData();
      if (!isCompanyPrivileged(userData?.role)) {
        return rejectWithValue("Access denied");
      }
      const rpcItems = items.map(({ formId, targetStatus }) => ({
        formId,
        targetStatus,
      }));

      const { data, error } = await supabase.rpc(
        "bulk_update_training_form_status_with_history",
        {
          p_items: rpcItems,
          p_comment: comment.trim(),
          p_created_by: userData.id,
        }
      );
      if (error) throw error;

      const requestEditFormIds = items
        .filter((item) => item.targetStatus === trainingFormStatus.REQUEST_EDIT)
        .map((item) => item.formId);

      if (requestEditFormIds.length) {
        const { data: forms, error: formsError } = await supabase
          .from("training_survey_form")
          .select(
            "id, company_id, company_user(first_name, last_name, email, department_id)"
          )
          .in("id", requestEditFormIds);

        if (!formsError && forms?.length) {
          const managerName =
            `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim() ||
            "Unknown";
          for (const form of forms) {
            if (form.company_user?.email) {
              sendEmailNotification(
                form.company_user.email,
                formType.REQUEST_EDIT,
                {
                  empName:
                    `${form.company_user.first_name || ""} ${form.company_user.last_name || ""}`.trim() ||
                    "Unknown",
                  dept_id: form.company_user.department_id,
                  managerName,
                  formId: form.id,
                  company_id: form.company_id,
                }
              );
            }
          }
        }
      }

      return {
        succeeded: data?.succeeded ?? items.length,
        failed: data?.failed ?? 0,
        errors: [],
      };
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchTrainingProgrammes = createAsyncThunk(
  "trainingForm/fetchTrainingProgrammes",
  async ({ requiredSkills, company_id, userDesignation, trainingType, programDuration }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/fetchTrainingProgrammes`,
        { requiredSkills, company_id, userDesignation, trainingType, programDuration },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_BACKEND_API_KEY}`,
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || "Failed to fetch training programmes");
      }

      // Check if the response data is valid
      if (!response.data.data || !Array.isArray(response.data.data)) {
        console.error("Invalid response format:", response.data);
        throw new Error("Invalid response format from server");
      }

      const formatData = response.data.data.map((item) => {
        return { ...item.payload, id: item.id };
      });

      // Show toast if no trainings are available
      if (!formatData || formatData.length === 0) {
        toast.info("No trainings available for the selected criteria.");
      }

      return formatData;
    } catch (error) {
      console.error("Error in fetchTrainingProgrammes:", error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to fetch training programmes";
      return rejectWithValue(errorMessage);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchEmployeeFormStatus = createAsyncThunk(
  "trainingForm/fetchEmployeeFormStatus",
  async (formIdOrObj, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      let formId, yearName;
      if (typeof formIdOrObj === 'object' && formIdOrObj !== null) {
        formId = formIdOrObj.formId;
        yearName = formIdOrObj.yearName;
      } else {
        formId = formIdOrObj;
        yearName = undefined;
      }
      
      // Join with company_user table to get user names
      const { data, error } = await supabase
        .from("form_status")
        .select(`
          *,
          from_user:from_id(first_name, last_name)
        `)
        .order("created_at", { ascending: false })
        .eq("form_id", formId);

      if (error) throw error;

      const formattedData = data.map((item) => {
        // Try to get year from yearName, or from due_date, or fallback to current year
        let year = yearName;
        if (!year) {
          if (item.due_date) {
            year = moment(item.due_date).format("YYYY");
          } else {
            year = moment().format("YYYY");
          }
        }
        
        // Format user names
        const fromUserName = item.from_user 
          ? `${item.from_user.first_name || ''} ${item.from_user.last_name || ''}`.trim()
          : '-';
        
        return {
          ...item,
          formName: `Training Needs Form ${year}`,
          dueDateFormatted: item.due_date
            ? moment(item.due_date).format("DD-MM-YYYY")
            : "-",
          updatedAtFormatted: item.updated_at
            ? moment(item.updated_at).format("DD-MM-YYYY")
            : "-",
          fromUserName,
        };
      });
      dispatch(stopLoading());
      return formattedData;
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchEmployeeForm = createAsyncThunk(
  "trainingForm/fetchEmployeeForm",
  async (formId, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data, error } = await supabase
        .from("training_survey_form")
        .select(
          "*,skill_requirements(*,selected_trainings(*,training_catalogue_data(*))),form_status(*),company_user(emp_id,employment_type,department_id)"
        )
        .eq("id", formId)
        .order("created_at", {
          ascending: false,
          referencedTable: "form_status",
        })
        .limit(1, { referencedTable: "form_status" })
        .single();

      if (error) throw error;

      let selectedRows = [];

      // Get training IDs only for null company_id in training_catalogue_data
      const trainingIds = [];
      if (data.skill_requirements) {
        data.skill_requirements.forEach(skill => {
          if (skill.selected_trainings) {
            skill.selected_trainings.forEach(training => {
              if (training.training_id && training.training_catalogue_data?.company_id === null) {
                trainingIds.push(training.training_id);
              }
            });
          }
        });
      }

      // Only fetch types if we have training IDs with null company_id
      let typeData = null;
      if (trainingIds.length > 0) {
        const { data: typesResult } = await fromWithCompany("training_catalogue_data_types")
          .select("training_id, type, company_id")
          .in("training_id", trainingIds)
          .eq("company_id", getLocalData().company_id);
        typeData = typesResult;
      }

      // Fetch allocated training category and type from allocate_training_program
      const selectedTrainingIds = [];
      if (data.skill_requirements) {
        data.skill_requirements.forEach(skill => {
          if (skill.selected_trainings) {
            skill.selected_trainings.forEach(training => {
              if (training.id) {
                selectedTrainingIds.push(training.id);
              }
            });
          }
        });
      }

      let allocatedData = null;
      if (selectedTrainingIds.length > 0) {
        const { data: allocatedResult } = await supabase
          .from("allocate_training_program")
          .select("selected_training_id, training_category, training_type, additional_field")
          .eq("form_id", formId)
          .in("selected_training_id", selectedTrainingIds);
        allocatedData = allocatedResult;
      }

      const formattedData = {
        formId: data.id,
        shortTermGoal: data.short_term_goal || "",
        longTermGoal: data.long_term_goal || "",
        AnyNotes: data.notes || "",
        skillsDetail: Array.isArray(data.skill_requirements)
          ? data.skill_requirements.map((skill, index) => {
              selectedRows.push({
                skill: skill.skills_required || "",
                selectedTrainingIds: [],
                selectedTrainingsInfo: [],
              });
              return {
                skillsRequired: skill.skills_required || "",
                trainingType: skill.training_type || "",
                priorityLevel: skill.priority_level || "",
                skillsRequirementMotivation:
                  skill.skills_requirement_motivation || "",
                completionDate: skill.completion_date || null,
                programmeDuration: skill.program_duration || "",
                // ...(configData.select_training
                //   ? { completionDate: skill.completion_date || null }
                //   : {}),
                // selectedTrainings: skill.selected_trainings || [],
                selectedTrainings: (skill?.selected_trainings ?? []).map(
                  (training) => {
                  selectedRows[index].selectedTrainingIds.push(
                    training.training_id
                  );

                  let type = null;
                  let company_id = null;
                  
                  if (training.training_catalogue_data?.company_id === null) {
                    const trainingTypeData = typeData?.find(t => t.training_id === training.training_id);
                    type = trainingTypeData?.type || null;
                    company_id = trainingTypeData?.company_id;
                  } else {
                    type = training.training_catalogue_data?.type || null;
                    company_id = training.training_catalogue_data?.company_id;
                  }

                  // Get allocated training category and type from allocate_training_program
                  const allocatedInfo = allocatedData?.find(
                    a => a.selected_training_id === training.id
                  );
                  
                  selectedRows[index].selectedTrainingsInfo.push({
                    ...training.training_catalogue_data,
                    id: training.training_id,
                    skill: skill.skills_required,
                    type: type,
                    company_id: company_id,
                    // Add allocated training category, type and additional fields
                    allocated_training_category: allocatedInfo?.training_category || null,
                    allocated_training_type: allocatedInfo?.training_type || null,
                    allocated_additional_field: allocatedInfo?.additional_field ?? null,
                  });
                  return training.training_id;
                },
                ),
              };
            })
          : [],
      };

      const rawToId = data.form_status?.[0]?.to_id;
      const deptLookupId =
        rawToId && String(rawToId).trim().toLowerCase() !== "null"
          ? rawToId
          : data.company_user?.department_id;
      const department = deptLookupId
        ? await dispatch(fetchOrganogramByDepartmentId(deptLookupId))
        : null;
      const latestFormStatus = data.form_status?.[0]?.status;

      return {
        formattedData,
        company_user: data.company_user,
        assignee_role: data.form_status?.[0]?.role,
        formId,
        to_id: data.form_status?.[0]?.to_id,
        emp_id: data.emp_id,
        form_status: latestFormStatus,
        selectedRows,
        dept_head_id:
          latestFormStatus === trainingFormStatus.REQUEST_EDIT ||
          latestFormStatus === trainingFormStatus.DRAFT
            ? data.company_user?.emp_id
            : department?.payload?.dept_head_id,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchEmployeeFormByYearId = createAsyncThunk(
  "trainingForm/fetchEmployeeFormByYearId",
  async ({ emp_id, year_id }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data, error } = await supabase
        .from("training_survey_form")
        .select(
          "*,skill_requirements(*,selected_trainings(*,training_catalogue_data(*))),form_status(*),company_user(emp_id,employment_type,department_id)"
        )
        .eq("emp_id", emp_id)
        .eq("year_id", year_id)
        .eq("status", statuses.ACTIVE)
        .order("created_at", {
          ascending: false,
          referencedTable: "form_status",
        })
        .limit(1, { referencedTable: "form_status" })
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        return null;
      }
//============================
      // Fetch allocated training category and type from allocate_training_program
      const selectedTrainingIds = [];
      if (data.skill_requirements) {
        data.skill_requirements.forEach(skill => {
          if (skill.selected_trainings) {
            skill.selected_trainings.forEach(training => {
              if (training.id) {
                selectedTrainingIds.push(training.id);
              }
            });
          }
        });
      }

      let allocatedData = null;
      if (selectedTrainingIds.length > 0) {
        const { data: allocatedResult } = await supabase
          .from("allocate_training_program")
          .select("selected_training_id, training_category, training_type, additional_field")
          .eq("form_id", data.id)
          .in("selected_training_id", selectedTrainingIds);
        allocatedData = allocatedResult;
      }
//====================
      let selectedRows = [];
      const formattedData = {
        shortTermGoal: data?.short_term_goal || "",
        longTermGoal: data?.long_term_goal || "",
        AnyNotes: data?.notes || "",
        // skillsDetail: Array.isArray(data.skill_requirements)
        //   ? data.skill_requirements.map((skill) => ({
        //       skillsRequired: skill.skills_required || "",
        //       trainingType: skill.training_type || "",
        //       priorityLevel: skill.priority_level || "",
        //       skillsRequirementMotivation:
        //         skill.skills_requirement_motivation || "",
        //       // ...(configData.select_training
        //       //   ? { completionDate: skill.completion_date || null }
        //       //   : {}),
        //       // selectedTrainings: Array.isArray(skill.selected_trainings)
        //       //   ? skill.selected_trainings.map((t) => {
        //       //       return t.training_id;
        //       //     })
        //       //   : [],
        //       // selectedTrainings: await fetchTrainingPrograms(
        //       //   Array.isArray(skill.selected_trainings)
        //       //     ? skill.selected_trainings.map((t) => {
        //       //         return t.training_id;
        //       //       })
        //       //     : []
        //       // ),
        //     }))
        //   : [],
        skillsDetail: Array.isArray(data?.skill_requirements)
          ? data.skill_requirements.map((skill, index) => {
          selectedRows.push({
            skill: skill.skills_required || "",
            selectedTrainingIds: [],
            selectedTrainingsInfo: [],
          });
          return {
            skillsRequired: skill?.skills_required || "",
            trainingType: skill?.training_type || "",
            priorityLevel: skill?.priority_level || "",
            skillsRequirementMotivation:
              skill?.skills_requirement_motivation || "",
            completionDate: skill?.completion_date || null,
            programmeDuration: skill?.program_duration || "",
            selectedTrainings: (skill?.selected_trainings ?? []).map(
              (training) => {
              selectedRows[index].selectedTrainingIds.push(
                training.training_id
              );

              // Get allocated training category and type from allocate_training_program
              const allocatedInfo = allocatedData?.find(
                a => a.selected_training_id === training.id
              );

              selectedRows[index].selectedTrainingsInfo.push({
                ...training.training_catalogue_data,
                id: training.training_id,
                skill: skill.skills_required,
                // Add allocated training category, type and additional fields
                allocated_training_category: allocatedInfo?.training_category || null,
                allocated_training_type: allocatedInfo?.training_type || null,
                allocated_additional_field: allocatedInfo?.additional_field ?? null,
              });
              return training.training_id;
            },
            ),
          };
        })
          : [],
      };

      const rawToId = data.form_status?.[0]?.to_id;
      const deptLookupId =
        rawToId && String(rawToId).trim().toLowerCase() !== "null"
          ? rawToId
          : data.company_user?.department_id;
      const department = deptLookupId
        ? await dispatch(fetchOrganogramByDepartmentId(deptLookupId))
        : null;
      const latestFormStatus = data.form_status?.[0]?.status;

      return {
        formattedData,
        assignee_role: data.form_status?.[0]?.role,
        formId: data.id,
        to_id: data.form_status?.[0]?.to_id,
        form_status: latestFormStatus,
        selectedRows,
        dept_head_id:
          latestFormStatus === trainingFormStatus.REQUEST_EDIT ||
          latestFormStatus === trainingFormStatus.DRAFT
            ? data.company_user?.emp_id
            : department?.payload?.dept_head_id,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchAllSelectedTrainings = createAsyncThunk(
  "trainingForm/fetchAllSelectedTrainings",
  async ({ company_id, year_id }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());

      const { data, error } = await supabase.rpc(
        "get_training_selection_counts_with_name",
        {
          p_company_id: company_id,
          p_year_id: year_id,
        }
      );

      if (error) throw error;

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchEmployeesWithoutForm = createAsyncThunk(
  "trainingForm/fetchEmployeesWithoutForm",
  async ({ companyId, yearId }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data, error } = await supabase.rpc("get_company_employees_without_form", {
        p_company_id: companyId,
        p_year_id: yearId,
      });
      if (error) throw error;
      return data ?? [];
    } catch (err) {
      return rejectWithValue(err.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

const trainingFormSlice = createSlice({
  name: "trainingForm",
  initialState: {
    trainingForm: null,
    selectedFormId: null,
    courses: [],
    employeeFormStatus: [],
    employeeForm: null,
    to_id: null,
    form_status: null,
  },
  reducers: {
    resetTrainingForm: (state) => {
      state.trainingForm = null;
      state.selectedFormId = null;
      state.courses = [];
      state.employeeFormStatus = [];
      state.to_id = null;
      state.form_status = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addTrainingForm.fulfilled, (state, action) => {
      state.trainingForm = action.payload.form;
      state.form_status = action.payload.form_status;
      state.selectedFormId = action.payload.form.id;
    });
    builder.addCase(fetchTrainingProgrammes.fulfilled, (state, action) => {
      state.courses = action.payload;
    });
    builder.addCase(fetchEmployeeFormStatus.fulfilled, (state, action) => {
      state.employeeFormStatus = action.payload;
    });
    builder.addCase(fetchEmployeeForm.fulfilled, (state, action) => {
      state.trainingForm = {
        ...action.payload.formattedData,
        company_user: action.payload.company_user,
      };
      state.selectedFormId = action.payload.formId;
      state.to_id = action.payload.to_id;
    });
    builder.addCase(fetchEmployeeFormByYearId.fulfilled, (state, action) => {
      if (action.payload) {
        state.trainingForm = action.payload.formattedData;
        state.selectedFormId = action.payload.formId;
        state.to_id = action.payload.to_id;
        state.form_status = action.payload.form_status;
      }
    });
  },
});
export const { resetTrainingForm } = trainingFormSlice.actions;
export default trainingFormSlice.reducer;
